import { tokenizedComponentClasses } from '@thyme/nashville/src/utils/designTokens'
import kebabCase from 'lodash-es/kebabCase'
import {
  computed,
  ExtractPropTypes,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue'
import { SHARED_PROP_KEYS, T_SPINNER_WITH_MESSAGES_PROPS } from './types'

type TMSpinnerWithMessagesProps = ExtractPropTypes<
  typeof T_SPINNER_WITH_MESSAGES_PROPS
>

/**
 *
 * @param props
 */
export function setup(props: TMSpinnerWithMessagesProps) {
  const tokenClasses = tokenizedComponentClasses(
    'spinner',
    props,
    SHARED_PROP_KEYS
  )

  const wrapperClasses = computed(() => {
    if (props.fullPage) return ['progress-spinner-wrapper']
    if (props.partialPage) return ['progress-spinner-wrapper-partial']
    return []
  })

  const currentIndex = ref(0)
  let timeoutId: ReturnType<typeof setTimeout> | null = null

  const messages = computed(() => props.messages)

  const currentMessage = computed(
    () => messages.value[currentIndex.value]?.text ?? ''
  )

  /**
   * Clears the current timer.
   */
  function clearTimer() {
    if (timeoutId) {
      clearTimeout(timeoutId)
      timeoutId = null
    }
  }

  /**
   * Schedules the next message to be shown.
   */
  function scheduleNext() {
    clearTimer()

    const len = messages.value.length
    const i = currentIndex.value

    // If we're at the last message, do not schedule further — stay there permanently
    if (i >= len - 1) return

    const ms = messages.value[i].ms
    timeoutId = setTimeout(() => {
      currentIndex.value = i + 1
      scheduleNext()
    }, ms)
  }

  onMounted(() => {
    if (messages.value.length > 1) scheduleNext()
  })

  // restart when messages changes
  watch(
    () => props.messages,
    () => {
      clearTimer()
      currentIndex.value = 0
      scheduleNext()
    },
    { deep: true }
  )

  onBeforeUnmount(clearTimer)

  return {
    wrapperClasses,
    tokenClasses,
    kebabCase,
    currentMessage,
    currentIndex,
  }
}
